/**
 *
 * @author Matthew Rawson @ 03/05/2018 16:50
 * @description
 * @notes
 */
import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import id from "@irati/utils/id"
import {injectIntl} from "@irati/utils/lang"
import {Card} from "@irati/ui/src/Layout"
import padding from "@irati/ui/src/global/padding"
import {getAverageScore} from "@irati/insights/helpers/question"
import {QuestionStatsContext} from "@irati/insights/context"
import {doIHavePermission} from "@irati/utils/permissions/HasPermissionTo"
import auth from "@irati/auth"
import {formatDriverDescription, formatDriverName} from "@irati/insights/utils/string"

import Table from "../../ui/SimpleTable"
import {CategoryTitle} from "../CategoryTitle"
import {QuestionDataRow} from "./"
import {ASSUMED_LIKERT_COUNT, ASSUMED_LIKERT_RANGE} from "./QuestionDataRow"

import helper from "../../helpers/DriverIcon"
import {LoadingOverlay} from "../../ui/LoadingOverlay"
import {ChangeOverTime} from "../../ui/PillBadge"
import {COT_TOOLTIP_CONTEXT_DRIVER} from "../../utils/constants/changeOverTime"


export const CARD_HEADINGS = (formatMessage) => ([
  {title: formatMessage({id: "insights.analysis.questions.column.questions"})},
  {title: formatMessage({id: "insights.analysis.questions.column.responses"}), right: true},
  {title: formatMessage({id: "insights.analysis.questions.column.skips"}), right: true},
  {title: formatMessage({id: "insights.analysis.questions.column.averageScore"}), right: true},
  ...ASSUMED_LIKERT_RANGE
])

/**
 * Styled comps
 */
const ReportingCard = styled(Card)`
  margin-bottom: ${padding.small}
`

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: bottom;
  margin-bottom: ${padding.small};
`

const Wrapper = styled.div`
  display: flex;
  align-items: flex-end;
`

/**
 * Component
 */
const QuestionReportingCard = ({driverQuestions, formatMessage, ...props}) => {
  const idNamespace = id("QuestionReportingCard", props)
  const hasQuestions = driverQuestions && driverQuestions.questions && driverQuestions.questions.length > 0
  const driverAnswerAverage = getAverageScore(driverQuestions.questions)
  const historicalDriverAnswerAverage = getAverageScore(driverQuestions.questions, "historicalQuestionStats")
  const driverAverageScore = isNaN(driverAnswerAverage) ? "N/A" : `${driverAnswerAverage.toFixed(1)}/${ASSUMED_LIKERT_COUNT}`
  const showCOT = doIHavePermission(["insights.changeOverTime"], auth.apps())

  return (
    <QuestionStatsContext.Consumer>
      {({loading, historicalLoading}) => {
        return (
          <ReportingCard id={idNamespace}>
            <LoadingOverlay loading={loading}>
              <TitleRow>
                <CategoryTitle
                  id={idNamespace}
                  category={formatMessage({id: "insights.global.keyDriver"})}
                  title={formatDriverName(formatMessage, driverQuestions.driver)}
                  icon={helper.getDriverIcon(driverQuestions.driver.contentId)}
                  tooltip={formatDriverDescription(formatMessage, driverQuestions.driver)} />
                <CategoryTitle
                  id={idNamespace}
                  category={formatMessage({id: "insights.analysis.questions.column.averageScore"})}
                  title={driverAverageScore}>
                  <Wrapper>
                    {showCOT && !loading && !historicalLoading && (
                      <ChangeOverTime
                        contextContentId={COT_TOOLTIP_CONTEXT_DRIVER}
                        currentScore={driverAnswerAverage}
                        historicalScore={historicalDriverAnswerAverage} />
                    )}
                  </Wrapper>
                </CategoryTitle>
              </TitleRow>
              <Table id={id("table", idNamespace)} headings={CARD_HEADINGS(formatMessage)} responsive>
                {hasQuestions && driverQuestions.questions.map((question) => (
                  <QuestionDataRow id={id(question.id, idNamespace)} key={question.id} question={question} />
                ))}
              </Table>
            </LoadingOverlay>
          </ReportingCard>
        )
      }}
    </QuestionStatsContext.Consumer>
  )
}


QuestionReportingCard.propTypes = {
  formatMessage: PropTypes.func.isRequired,
  driverQuestions: PropTypes.object
}
QuestionReportingCard.defaultProps = {
  driverQuestions: {}
}

export default injectIntl(QuestionReportingCard)
